/**
 * Headless template loader (US 359).
 *
 * There are three templates, composed from four directories:
 *
 *   headless-template-shared/   the build config, the preview bridge, index.css, cn()
 *   headless-template-web/      + the shadcn kit, react-router-dom, sonner
 *   headless-template-mobile/   + Framework7 and its icon fonts, and nothing web
 *   headless-template-website/  laid over web: a card without a shadow, and src/components/site
 *
 * The website template installs exactly what web does, so it has no package.json of its own and
 * previews with the web vendor bundle (`vendorTemplateFor`). What it changes is a few files: the
 * shadow a website could not be prompted out of, and the building blocks it kept getting wrong.
 *
 * The split exists because a prompt could not hold the line. A mobile screen that imported
 * `useNavigate` compiled perfectly well while react-router was installed, and only failed at
 * render — "useNavigate() may be used only in the context of a <Router>" — because the seeded
 * shell mounts Framework7's `<App>` and `<View>`, not a Router. With the dependency gone the
 * import stops resolving, which is a failure the build can see and repair.
 *
 * Shared files live in one place rather than being copied into both trees: they are the files
 * that have nothing to do with which component library a prototype uses, and duplicating them
 * is how the two drift apart.
 *
 * `getHeadlessTemplateFiles()` keeps its signature and reads the active template, because it is
 * called from a dozen places (write tools, prune, zip download, WebContainer boot) that have no
 * business knowing the mode. `setActiveTemplate` is called once when a project is opened or a
 * build starts; the workbench is single-project, exactly as `workbenchStore` already assumes.
 */

import { atom } from 'nanostores';

export type TemplateId = 'web' | 'mobile' | 'website';

const SHARED_PREFIX = './headless-template-shared/';
type VariantDir = 'web' | 'mobile' | 'website';

const VARIANT_PREFIX: Record<VariantDir, string> = {
  web: './headless-template-web/',
  mobile: './headless-template-mobile/',
  website: './headless-template-website/',
};

const sharedModules: Record<string, string> = import.meta.glob(
  './headless-template-shared/**/*',
  { eager: true, query: '?raw', import: 'default' },
) as Record<string, string>;

const webModules: Record<string, string> = import.meta.glob(
  './headless-template-web/**/*',
  { eager: true, query: '?raw', import: 'default' },
) as Record<string, string>;

const mobileModules: Record<string, string> = import.meta.glob(
  './headless-template-mobile/**/*',
  { eager: true, query: '?raw', import: 'default' },
) as Record<string, string>;

const websiteModules: Record<string, string> = import.meta.glob(
  './headless-template-website/**/*',
  { eager: true, query: '?raw', import: 'default' },
) as Record<string, string>;

const VARIANT_MODULES: Record<VariantDir, Record<string, string>> = {
  web: webModules,
  mobile: mobileModules,
  website: websiteModules,
};

/** The directories laid over the shared one, in order: a later one wins a collision. */
const LAYERS: Record<TemplateId, readonly VariantDir[]> = {
  web: ['web'],
  mobile: ['mobile'],
  website: ['web', 'website'],
};

/** The vendor bundle a template previews with: a website installs what web does. */
export function vendorTemplateFor(id: TemplateId): 'web' | 'mobile' {
  return id === 'mobile' ? 'mobile' : 'web';
}

/**
 * Chef appends its website motion to a website's seeded `index.css` (`websiteMotion.ts`), and to
 * no other mode's, so the marker says which template a prototype's files belong to.
 */
const WEBSITE_CSS_MARKER = '/* ideoz:website-motion */';

/**
 * Web is the default because every mode except `mobile` uses it, and because a wrong guess there
 * is a prototype with spare dependencies rather than one that cannot resolve its imports.
 */
/**
 * A store, not a variable: the file tree and the editor list the active template's files next to
 * the project's own, and have to redraw when it changes. As a plain variable they kept showing
 * the web template's package.json and src/components/ui on a mobile prototype until something
 * else happened to re-render them (27 Sep 2026).
 */
export const activeTemplateStore = atom<TemplateId>('web');

const _cache = new Map<TemplateId, Record<string, string>>();

function strip(key: string, prefix: string): string {
  return key.startsWith(prefix) ? key.slice(prefix.length) : key;
}

/** Which template new seeding and template-file lookups use. */
export function getActiveTemplate(): TemplateId {
  return activeTemplateStore.get();
}

/** Set once per project/build. Safe to call repeatedly with the same value. */
export function setActiveTemplate(id: TemplateId): void {
  if (activeTemplateStore.get() !== id) activeTemplateStore.set(id);
}

/**
 * The template a set of project files belongs to.
 *
 * Read from the source as well as `package.json`, the same rule Chef's `templateDirs.ts` uses.
 * `package.json` alone was wrong in practice: the workbench store never holds one - it comes from
 * the active template, which defaults to web - so a mobile prototype resolved as web and the
 * preview loaded the bundle without Framework7 in it. The seeded mobile shell always imports
 * `framework7/bundle`, and nothing in either template does, so that import is the fact to read.
 *
 * The website marker is read first. Every website run seeds it and a phone app's seed never
 * carries it, while a phone app's screens can outlive it in a project regenerated as a website:
 * ConnectHub (1 Oct 2026) was previewed with the mobile template, which has no site kit, and
 * crashed on `@/components/site/SiteHeader`. Chef now drops those screens too.
 */
export function templateIdForFiles(files: Record<string, string> | undefined): TemplateId {
  if (!files) return 'web';
  if (files['src/index.css']?.includes(WEBSITE_CSS_MARKER)) return 'website';
  let hasSource = false;
  for (const [path, content] of Object.entries(files)) {
    if (!/^src\/.*\.(tsx?|jsx?)$/i.test(path)) continue;
    hasSource = true;
    if (/from\s+['"]framework7(?:\/|-react|['"])/.test(content)) return 'mobile';
  }
  // package.json only decides when there is no source to read. A version's zip carries the
  // template it was saved with, and a web app saved while the tab still held a phone app's
  // package.json came back as mobile on every open (TeamTask, 2 Oct 2026); a phone app's
  // App.tsx always imports Framework7, so the source is the better witness.
  const pkg = files['package.json'];
  if (!hasSource && pkg && /"framework7"\s*:/.test(pkg)) return 'mobile';
  return 'web';
}

/** The template a prototyper mode builds with. */
export function templateIdForMode(mode: string | undefined): TemplateId {
  if (mode === 'mobile') return 'mobile';
  return mode === 'website' ? 'website' : 'web';
}

/**
 * Returns `{ "package.json": "...", "src/App.tsx": "...", ... }` for one template: the shared
 * files with the variant's files layered over them. Cached per template after first call.
 */
export function getHeadlessTemplateFiles(
  id: TemplateId = activeTemplateStore.get(),
): Record<string, string> {
  const cached = _cache.get(id);
  if (cached) return cached;

  const files: Record<string, string> = {};
  for (const [key, content] of Object.entries(sharedModules)) {
    files[strip(key, SHARED_PREFIX)] = content;
  }
  for (const layer of LAYERS[id]) {
    for (const [key, content] of Object.entries(VARIANT_MODULES[layer])) {
      files[strip(key, VARIANT_PREFIX[layer])] = content;
    }
  }

  _cache.set(id, files);
  return files;
}
